/**
 * Recharge customer portal - hide the cancel action on subscriptions that are
 * not cancellable yet.
 *
 * A subscription is locked when it is prepaid (paid up front for several
 * deliveries) or when it expires on its own after a set number of charges. Both
 * carry a minimum commitment, so the customer must not be able to cancel before
 * it is fulfilled.
 *
 * Three surfaces are handled:
 *   - the subscription list   [data-testid="subscription-<id>"]
 *   - the subscription detail [data-recharge-subscription="<id>"], which can be
 *     opened from several places, so it is detected on its own rather than via
 *     the active tab, and whose id comes from the portal URL
 *   - the "Verwalten" dropdown, which react-aria renders into <body> outside the
 *     portal and which therefore has to be attributed back to a card
 *
 * The portal is a third-party React app, so the cancel action has to be removed
 * from the rendered DOM. Which subscriptions to remove it from comes from the
 * Recharge API, because "expires after N charges" has no reliable DOM
 * representation. When the API is unreachable the script falls back to reading
 * the portal's own translated strings, which still covers the prepaid case but
 * not the expiring one.
 *
 * Nothing here matches on the portal's generated CSS class names - those change
 * with every Recharge build.
 */
(() => {
  const SELECTORS = {
    portal: '#recharge-customer-portal',
    navItem: '[role="tab"][data-key]',
    subscriptionsNavItem: '[role="tab"][data-key="/subscriptions"]',
    listCard: '[data-testid^="subscription-"]',
    detail: '[data-recharge-subscription], [data-testid="EditSubscription"]',
    cancelBlock: '[data-recharge-subscription-cancel]',
    overlay: '[data-overlay-container]',
    badge: '.recharge-badge',
    // react-aria renders a dropdown as a menu, so its entries are menuitems
    // rather than buttons.
    control:
      'button, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"]',
  };

  const LIST_CARD_TESTID = /^subscription-\d+$/;
  const HIDDEN_MARK = 'data-tisso-hidden';
  const BOOT_TIMEOUT = 30000;

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  // ---------------------------------------------------------------------------
  // Recharge API
  //
  // The portal authenticates with a short-lived JWT in the x-recharge-access-token
  // header plus a shop_url parameter. That JWT is not window.ReCharge.session.token
  // and there is no documented way to mint one from the theme, so instead of
  // guessing at an auth exchange we read the credentials off the portal's own
  // requests: window.fetch is wrapped as a pass-through that records the header
  // and the shop_url, and indexes any subscription objects that come back.
  //
  // Consequences worth knowing: the token is always as fresh as the portal's own
  // (it rotates roughly hourly), and if the portal never calls the API in this
  // page view we simply fall back to the DOM heuristics.
  // ---------------------------------------------------------------------------

  const API_HOST = /(^|\.)rechargeapps\.com$/i;
  const API_BASE = 'https://api.rechargeapps.com/subscriptions';
  const TOKEN_HEADER = 'x-recharge-access-token';
  // Only fields that identify the subscription itself. Collecting every *_id
  // would drag customer_id and address_id into the index, where they are shared
  // across subscriptions and can only cause false matches.
  const ID_FIELD = /^(id|purchase_item_id|subscription_id|external_subscription_id)$/;

  const credentials = { token: null, shopUrl: null };
  const api = { state: 'idle', locked: new Set(), known: new Set() };

  const resolveUrl = (input) => {
    const raw = typeof input === 'string' ? input : input?.url;
    if (!raw) return null;
    try {
      return new URL(raw, window.location.href);
    } catch {
      return null;
    }
  };

  /**
   * init.headers may be a Headers instance, a plain object or an array of pairs,
   * and the header may equally sit on a Request passed as the first argument.
   */
  const readHeader = (input, init, name) => {
    const sources = [init?.headers, typeof input === 'object' ? input?.headers : null];
    for (const source of sources) {
      if (!source) continue;
      if (typeof source.get === 'function') {
        const value = source.get(name);
        if (value) return value;
      } else if (Array.isArray(source)) {
        const pair = source.find(([key]) => String(key).toLowerCase() === name);
        if (pair) return pair[1];
      } else if (typeof source === 'object') {
        for (const [key, value] of Object.entries(source)) {
          if (String(key).toLowerCase() === name && value) return value;
        }
      }
    }
    return null;
  };

  const shopUrl = () =>
    credentials.shopUrl || window.Shopify?.shop || null;

  const customerId = () => {
    const fromSession = window.ReCharge?.session?.customer_id;
    if (fromSession) return String(fromSession);
    // The portal JWT carries the customer id in its payload.
    try {
      const payload = credentials.token?.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded.customer_id ? String(decoded.customer_id) : null;
    } catch {
      return null;
    }
  };

  const toList = (payload) => {
    if (Array.isArray(payload)) return payload;
    for (const key of ['subscriptions', 'data', 'results', 'items']) {
      if (Array.isArray(payload?.[key])) return payload[key];
    }
    // A single-subscription response, as the detail view fetches it.
    if (payload?.subscription) return [payload.subscription];
    if (payload?.id && 'order_interval_frequency' in payload) return [payload];
    return null;
  };

  /**
   * Prepaid: charged less often than it ships, i.e. one payment covers several
   * deliveries. Expiring: ends by itself after a set number of charges. Either
   * way the customer is committed until the cycle is over.
   *
   * Deliberately does NOT consult is_prepaid. The "Convert prepaid subscription
   * to monthly after final delivery" Flow rewrites a fulfilled prepaid plan to
   * charge_interval_frequency: 1 / order_interval_frequency: 1 without clearing
   * is_prepaid, so that flag can outlive the commitment it describes and would
   * lock such a subscription forever. The interval pair is current state and the
   * Flow keeps it truthful; every prepaid subscription has charge > order
   * anyway, so nothing is lost by ignoring the flag.
   */
  const isLocked = (subscription) => {
    // Must be a positive count: Recharge reports "no expiry" as 0 or null, and
    // treating 0 as an expiry locks every ordinary subscription.
    const expiresAfter = Number(subscription.expire_after_specific_number_of_charges);
    if (Number.isFinite(expiresAfter) && expiresAfter > 0) return true;

    // Charged less often than it ships: one payment covers several deliveries.
    // Both frequencies share order_interval_unit, so the numbers are comparable.
    const charge = Number(subscription.charge_interval_frequency);
    const order = Number(subscription.order_interval_frequency);
    return Number.isFinite(charge) && Number.isFinite(order) && charge > order;
  };

  /**
   * The list and the detail view identify the same subscription with different
   * ids, so every id-shaped field is indexed rather than just `id`.
   */
  const collectIds = (subscription, target) => {
    for (const [key, value] of Object.entries(subscription)) {
      if (!ID_FIELD.test(key)) continue;
      if (typeof value === 'number' || typeof value === 'string') target.add(String(value));
    }
  };

  /**
   * Additive, because subscriptions reach us from two places: our own list call
   * and whatever the portal happens to fetch.
   */
  const merge = (subscriptions) => {
    let added = 0;
    for (const subscription of subscriptions) {
      if (!subscription || typeof subscription !== 'object') continue;
      collectIds(subscription, api.known);
      if (isLocked(subscription)) collectIds(subscription, api.locked);
      added++;
    }
    if (!added) return false;
    api.state = 'ready';
    return true;
  };

  const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  const listUrl = () => {
    const params = new URLSearchParams({ limit: '250' });
    const shop = shopUrl();
    const customer = customerId();
    if (shop) params.set('shop_url', shop);
    if (customer) params.set('customer_id', customer);
    return `${API_BASE}?${params}`;
  };

  /**
   * Fetches the full list once credentials are available. The portal only loads
   * what it renders, so this is what guarantees every subscription is classified.
   */
  const load = async () => {
    if (api.loaded || !nativeFetch || !credentials.token) return;
    api.loaded = true;

    try {
      const response = await nativeFetch(listUrl(), {
        headers: {
          accept: 'application/json',
          [TOKEN_HEADER]: credentials.token,
          'x-recharge-version': '2021-11',
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const list = toList(await response.json());
      if (!list) throw new Error('unrecognised payload');
      merge(list);
    } catch {
      // The sniffed responses remain: only merge() promotes the state, so a
      // failure here cannot undo what the portal's own traffic already told us.
    }
    schedule();
  };

  const sniffResponse = (response, url) => {
    response
      .clone()
      .json()
      .then((payload) => {
        // Only the responses that actually carry subscriptions are worth a line -
        // the portal calls a dozen other endpoints on every view.
        const list = toList(payload);
        if (list?.length && merge(list)) schedule();
      })
      .catch(() => {
        /* not JSON, or already consumed - nothing to learn here */
      });
  };

  /**
   * Pass-through wrapper. It never alters the request, never delays the response
   * and swallows its own errors, so the portal cannot break because of it.
   */
  const interceptFetch = () => {
    if (!nativeFetch) return;
    window.fetch = function (input, init) {
      let url = null;
      try {
        url = resolveUrl(input);
        if (url && API_HOST.test(url.hostname)) {
          const token = readHeader(input, init, TOKEN_HEADER);
          if (token) credentials.token = token;
          const shop = url.searchParams.get('shop_url');
          if (shop) credentials.shopUrl = shop;
          if (credentials.token) load();
        }
      } catch {
        url = null;
      }

      const result = nativeFetch(input, init);
      if (url && API_HOST.test(url.hostname)) {
        const sniffUrl = url;
        result.then((response) => sniffResponse(response, sniffUrl)).catch(() => {});
      }
      return result;
    };
  };


  // ---------------------------------------------------------------------------
  // DOM heuristics (fallback)
  // ---------------------------------------------------------------------------

  // A translation short enough to be an ordinary word ("Bezahlt" is a substring
  // of "Vorausbezahlt", and of any payment wording) is only trusted inside a
  // status badge. Longer strings are distinctive enough for full text.
  const BROAD_PATTERN_MIN_LENGTH = 12;

  const LOCK_KEY = /prepaid|expir/i;
  const FALLBACK_LOCK = { broad: [/prepaid/i], badge: [/prepaid/i] };
  const FALLBACK_CANCEL = [
    'kündigen',
    'kuendigen',
    'abo kündigen',
    'sparplan kündigen',
    'cancel',
    'cancel subscription',
  ];

  /**
   * Flattens window.ReCharge.translations into [keyPath, string] pairs. The
   * nesting depth is not documented, so we walk whatever we are given.
   */
  const flattenTranslations = (node, path = [], out = []) => {
    if (typeof node === 'string') {
      out.push([path.join('.'), node]);
    } else if (node && typeof node === 'object') {
      for (const [key, value] of Object.entries(node)) {
        flattenTranslations(value, path.concat(key), out);
      }
    }
    return out;
  };

  const getTranslations = () => {
    try {
      return flattenTranslations(window.ReCharge?.translations);
    } catch {
      return [];
    }
  };

  /**
   * Turns "Prepaid: Shipment {current} of {total}" into a regex that matches the
   * rendered "Prepaid: Shipment 2 of 4".
   */
  const toPattern = (value) => {
    const text = normalize(value);
    if (text.length < 4) return null;
    const source = text
      .split(/\{[^}]*\}/g)
      .map((part) => normalize(part).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .filter((part) => part.length)
      .join('[\\s\\S]{0,40}?');
    return source ? new RegExp(source, 'i') : null;
  };

  /**
   * broad: matched against the whole subscription element.
   * badge: matched against status badges only.
   */
  const buildLockPatterns = () => {
    const broad = [];
    const badge = [];
    for (const [key, value] of getTranslations()) {
      if (!LOCK_KEY.test(key) && !LOCK_KEY.test(value)) continue;
      const pattern = toPattern(value);
      if (!pattern) continue;
      badge.push(pattern);
      if (normalize(value).length >= BROAD_PATTERN_MIN_LENGTH) broad.push(pattern);
    }
    if (!badge.length) return FALLBACK_LOCK;
    return { broad, badge };
  };

  /**
   * Button labels only - a translation that reads like a sentence belongs to a
   * heading or a confirmation dialog, not to the control we are looking for.
   */
  /**
   * A translation filed under a cancel key is not necessarily a cancel button.
   * The live table contains "keep subscription", "nein, Adresse beibehalten",
   * "Kündigungsrabatt" and the past-tense confirmations - some of them the exact
   * opposite action. Anything that keeps something, answers a dialog, reports a
   * finished cancellation or offers a discount is rejected.
   */
  const NOT_A_CANCEL_LABEL = /\bkeep\b|behalt|gekündigt|gekuendigt|cancell?ed|storniert|rabatt|discount|,/i;

  const buildCancelLabels = () => {
    const labels = new Set(FALLBACK_CANCEL);
    for (const [key, value] of getTranslations()) {
      if (!/cancel/i.test(key)) continue;
      const text = normalize(value).toLowerCase();
      if (!text || text.length > 40) continue;
      if (/[.?!{]/.test(text)) continue;
      if (NOT_A_CANCEL_LABEL.test(text)) continue;
      labels.add(text);
    }
    return labels;
  };

  let lockPatterns = null;
  let cancelLabels = null;

  const matchesLock = (root) => {
    lockPatterns ||= buildLockPatterns();
    const text = normalize(root.textContent);
    if (lockPatterns.broad.some((pattern) => pattern.test(text))) return true;
    return [...root.querySelectorAll(SELECTORS.badge)].some((badge) => {
      const badgeText = normalize(badge.textContent);
      return lockPatterns.badge.some((pattern) => pattern.test(badgeText));
    });
  };

  const domSaysLocked = (root) => {
    if (matchesLock(root)) return true;

    // In the list the prepaid notice sometimes renders as a sibling of the card
    // rather than inside it. Only widen the search when the wrapper holds this
    // card alone, so we never read a neighbouring subscription's text.
    const wrapper = root.parentElement;
    if (!wrapper || wrapper.querySelectorAll(SELECTORS.listCard).length !== 1) return false;
    return matchesLock(wrapper);
  };

  // ---------------------------------------------------------------------------
  // DOM manipulation
  // ---------------------------------------------------------------------------

  /**
   * An icon-only control carries its label in aria-label rather than in text,
   * which is how the dropdown entries reach us.
   */
  const controlLabel = (control) =>
    normalize(control.textContent) ||
    normalize(control.getAttribute('aria-label')) ||
    normalize(control.getAttribute('title'));

  const isCancelControl = (control) => {
    cancelLabels ||= buildCancelLabels();
    return cancelLabels.has(controlLabel(control).toLowerCase());
  };

  /**
   * The control sits inside layout wrappers that carry the flex gap. Hiding the
   * control alone would leave a hole, so we climb to the outermost ancestor that
   * exists solely to wrap it. Because every step requires a single child, this
   * can never hide a sibling.
   */
  const hideTargetFor = (element, root) => {
    let target = element;
    while (
      target.parentElement &&
      target.parentElement !== root &&
      target.parentElement.childElementCount === 1
    ) {
      target = target.parentElement;
    }
    return target;
  };

  /**
   * Re-applies unconditionally rather than trusting the marker: a re-render can
   * hand back a node that kept our attributes but lost the inline style, and
   * bailing out on the marker alone would leave the control visible. The return
   * value only says whether this was the first time, to keep the log readable.
   */
  const hide = (element) => {
    const first = !element.hasAttribute(HIDDEN_MARK);
    element.setAttribute(HIDDEN_MARK, '');
    element.style.setProperty('display', 'none', 'important');
    element.setAttribute('aria-hidden', 'true');
    return first;
  };

  /**
   * Only ever reverses our own change, so a subscription the API clears after
   * the DOM fallback guessed otherwise gets its cancel action back.
   */
  const restore = (root) => {
    for (const element of root.querySelectorAll(`[${HIDDEN_MARK}]`)) {
      element.style.removeProperty('display');
      element.removeAttribute('aria-hidden');
      element.removeAttribute(HIDDEN_MARK);
    }
  };

  /**
   * The detail view wraps its cancel action in a dedicated block. Where that
   * exists it is authoritative, and we skip label matching - a confirmation
   * dialog's own "Cancel" must not be caught.
   */
  const hideCancel = (root) => {
    // A collapsed list card legitimately has nothing to hide: it only renders
    // "Verwalten", and the cancel action lives in the popover react-aria mounts
    // on press. The loop is then simply a no-op.
    const block = root.querySelector(SELECTORS.cancelBlock);
    const targets = block
      ? [block]
      : [...root.querySelectorAll(SELECTORS.control)].filter(isCancelControl);

    for (const target of targets) hide(hideTargetFor(target, root));
  };

  // The portal URL carries the Recharge subscription id:
  //   /tools/recurring/portal/<hash>/subscriptions/856755023?token=...
  const DETAIL_URL_ID = /\/subscriptions\/(\d+)/;

  const idFromUrl = () => DETAIL_URL_ID.exec(window.location.pathname)?.[1] || null;

  /**
   * Every id this element could be known by, best first. The list card's
   * data-testid holds the Recharge id, but the detail view's
   * data-recharge-subscription is a different id space that the API objects do
   * not carry - there the URL is what matches.
   */
  const candidateIds = (element) => {
    const testid = element.dataset.testid || '';
    if (LIST_CARD_TESTID.test(testid)) return [testid.replace('subscription-', '')];

    const ids = [];
    const fromUrl = idFromUrl();
    if (fromUrl) ids.push(fromUrl);
    if (element.dataset.rechargeSubscription) ids.push(element.dataset.rechargeSubscription);
    return ids;
  };

  const isLockedElement = (root) => {
    if (api.state === 'ready') {
      const ids = candidateIds(root);
      if (ids.some((id) => api.locked.has(id))) return true;
      // The API answered for this subscription and did not lock it.
      if (ids.some((id) => api.known.has(id))) return false;
      // Unknown to the API - fall through to the text heuristics.
    }
    return domSaysLocked(root);
  };

  const processSubscription = (root) => {
    if (isLockedElement(root)) hideCancel(root);
    else restore(root);
  };

  const isSubscriptionsTabActive = (portal) => {
    // If the portal renders without the tab navigation we do not block on it.
    if (!portal.querySelector(SELECTORS.navItem)) return true;
    return portal.querySelector(`${SELECTORS.subscriptionsNavItem}[aria-selected="true"]`) !== null;
  };

  // ---------------------------------------------------------------------------
  // Overlays
  //
  // The "Verwalten" dropdown is a react-aria popover, so it renders into a
  // portal at the end of <body>, outside #recharge-customer-portal, and carries
  // no subscription id of its own. It is attributed back to a card two ways:
  // through the trigger's aria-controls where react-aria sets it, and otherwise
  // through the card whose control was pressed last.
  // ---------------------------------------------------------------------------

  // The id, not the element: the popover mounts a tick after the press, and
  // React may well have replaced the card node in between. A remembered node
  // would be detached by then and the overlay would go unattributed.
  let lastPressedId = null;

  const rememberPressedCard = (event) => {
    const card = event.target?.closest?.(SELECTORS.listCard);
    if (!card || !LIST_CARD_TESTID.test(card.dataset.testid || '')) return;
    const id = card.dataset.testid.replace('subscription-', '');
    if (id === lastPressedId) return;
    lastPressedId = id;
    schedule();
  };

  // Digits only, which every list card id is - so the value can go straight
  // into the selector without needing CSS.escape, which jsdom and older
  // browsers do not provide.
  const cardById = (id) =>
    id && /^\d+$/.test(id) ? document.querySelector(`[data-testid="subscription-${id}"]`) : null;

  /** null when neither the card nor the API can answer for this id. */
  const lockedForId = (id) => {
    const card = cardById(id);
    if (card) return isLockedElement(card);
    if (api.state !== 'ready') return null;
    if (api.locked.has(id)) return true;
    if (api.known.has(id)) return false;
    return null;
  };

  /** The popover react-aria links to an open trigger inside this card, if any. */
  const linkedOverlay = (card) => {
    const trigger = card.querySelector('[aria-expanded="true"][aria-controls]');
    if (!trigger) return null;
    const overlay = document.getElementById(trigger.getAttribute('aria-controls'));
    return overlay && !card.contains(overlay) ? overlay : null;
  };

  const detachedOverlays = (portal) =>
    [...document.querySelectorAll(SELECTORS.overlay)].filter(
      (overlay) => !portal.contains(overlay) && overlay.childElementCount > 0
    );

  const processOverlays = (portal, cards) => {
    const roots = detachedOverlays(portal);
    if (!roots.length) return;

    // aria-controls points at the popover itself, which sits inside one of the
    // overlay containers - so the link is resolved up to its container.
    const linked = new Map();
    for (const card of cards) {
      const popover = linkedOverlay(card);
      if (!popover) continue;
      const root = roots.find((candidate) => candidate.contains(popover));
      if (root) linked.set(root, card.dataset.testid.replace('subscription-', ''));
    }

    for (const overlay of roots) {
      // Without an aria link, the only honest attribution is the card the
      // customer just pressed. With neither, leave the overlay alone rather
      // than hide a cancel action that might belong to another subscription.
      const id = linked.get(overlay) || lastPressedId;
      if (!id) continue;

      const locked = lockedForId(id);
      if (locked === null) continue;

      if (locked) hideCancel(overlay);
      else restore(overlay);
    }
  };

  const process = () => {
    const portal = document.querySelector(SELECTORS.portal);
    if (!portal) return;

    // The detail view opens from several places, so it is not tied to a tab.
    const details = [...portal.querySelectorAll(SELECTORS.detail)];
    const cards = isSubscriptionsTabActive(portal)
      ? [...portal.querySelectorAll(SELECTORS.listCard)].filter((card) =>
          LIST_CARD_TESTID.test(card.dataset.testid || '')
        )
      : [];

    if (!details.length && !cards.length) return;
    load();

    for (const detail of details) processSubscription(detail);
    for (const card of cards) processSubscription(card);
    processOverlays(portal, cards);
  };

  let frame = null;
  const schedule = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      process();
    });
  };

  const observe = () => {
    // Watches the whole body, not just the portal: the "Verwalten" popover is
    // rendered outside it. childList for the tree, plus aria-selected for tab
    // switches and aria-expanded for popovers opening. We never write any of
    // those, so this cannot feed back into itself - the inline style and the
    // attributes we do write are not observed.
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // aria-controls included because it is what ties a popover to its card;
      // if it moves to another card we have to re-evaluate. None of these are
      // attributes we write ourselves.
      attributeFilter: ['aria-selected', 'aria-expanded', 'aria-controls'],
    });
    // Capture phase, because react-aria stops propagation on its own triggers.
    document.addEventListener('pointerdown', rememberPressedCard, true);
    document.addEventListener('click', rememberPressedCard, true);
    schedule();
  };

  const start = () => {
    const portal = document.querySelector(SELECTORS.portal);
    if (!portal) return false;
    observe();
    return true;
  };

  // Wrapped before anything else: the portal's first API call is what hands us
  // the credentials, and a call made before this point is one we cannot read.
  interceptFetch();

  if (!start()) {
    // The portal mounts asynchronously. Watch for it, but give up rather than
    // keeping a document-wide observer alive on every page of the shop.
    const bootObserver = new MutationObserver(() => {
      if (start()) bootObserver.disconnect();
    });
    bootObserver.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => bootObserver.disconnect(), BOOT_TIMEOUT);
  }
})();
